// ==========================================================================
// TEMPLATE LOADER - Load shared components
// ==========================================================================

// Function to load HTML components with enhanced error handling
async function loadComponent(elementId, componentPath) {
    const element = document.getElementById(elementId);
    if (!element) {
        console.warn(`Element with ID '${elementId}' not found`);
        return false;
    }

    // Show loading state — styled via the .loading-state class so no inline
    // CSS is needed (CSP-friendly).
    const originalContent = element.innerHTML;
    element.innerHTML = '<div class="loading-state">Loading…</div>';

    try {
        const response = await fetch(componentPath);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to load ${componentPath}`);
        }

        const html = await response.text();
        if (!html.trim()) {
            throw new Error(`Empty response from ${componentPath}`);
        }

        element.innerHTML = html;
        return true;

    } catch (error) {
        console.error('Error loading component:', error);

        // Build the error UI with DOM APIs instead of innerHTML so no inline
        // styles or `onclick=` attributes need to survive the CSP. Styles
        // come from the .error-state / .retry-btn classes in
        // src/scss/components/_template-error.scss.
        element.innerHTML = '';
        const wrap = document.createElement('div');
        wrap.className = 'error-state';

        const msg = document.createElement('p');
        msg.className = 'error-state-msg';
        msg.textContent = `Failed to load component: ${componentPath.split('/').pop()}`;
        wrap.appendChild(msg);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'error-state-retry';
        btn.textContent = 'Retry';
        btn.addEventListener('click', () => loadComponent(elementId, componentPath));
        wrap.appendChild(btn);

        element.appendChild(wrap);

        return false;
    }
}

// Function to set active navigation states
function setActiveNavigation(currentPage) {
    // Wait for navigation to be loaded
    setTimeout(() => {
        const navLinks = document.querySelectorAll('nav a[data-page]');
        const dropdownLinks = document.querySelectorAll('.dropdown-menu a[data-page]');

        navLinks.forEach(link => {
            const page = link.getAttribute('data-page');
            if (page === currentPage) {
                link.classList.add('active');
                // If it's any service page/sub-page, also mark the Services dropdown trigger as active
                const servicePages = [
                    'web-design', 'digital-marketing', 'brand-strategy', 'performance-analytics',
                    'mobile-app-development',
                    'ios-android-development', 'progressive-web-apps', 'app-ui-ux-design',
                    'custom-website-design', 'best-practice', 'e-commerce-and-online-stores',
                    'content-management', 'website-maintenance-care-plans', 'performance-optimisation',
                    'search-engine-optimisation', 'pay-per-click-advertising', 'email-marketing',
                    'social-media-marketing', 'content-marketing', 'local-seo',
                    'brand-discovery-research', 'brand-positioning', 'visual-identity-design',
                    'brand-messaging', 'brand-guidelines', 'brand-implementation'
                ];
                if (servicePages.includes(currentPage)) {
                    const servicesLink = document.querySelector('nav a[data-page="services"]');
                    if (servicesLink) {
                        servicesLink.classList.add('active');
                    }
                }
            } else {
                link.classList.remove('active');
            }
        });

        // Handle dropdown menu items
        dropdownLinks.forEach(link => {
            const page = link.getAttribute('data-page');
            if (page === currentPage) {
                link.classList.add('active');
            } else {
                link.classList.remove('active');
            }
        });
    }, 100);
}

// Cache-bust version shared by all partial fetches (header/footer/mega-menu).
// Keep in sync with the preload tags in each page's <head>.
const PARTIALS_VERSION = '1780400000';

// Lazy-load Google's <model-viewer> element (~500 KB+ ES module). Only loaded
// when the page actually has <model-viewer> elements AND one is about to come
// into view OR a 3D modal trigger is clicked. Until then, the elements render
// as plain unknown-element boxes; they upgrade automatically once the module
// finishes loading.
function lazyLoadModelViewer() {
    if (!document.querySelector('model-viewer')) return;

    const SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
    let loading = false;

    function load() {
        if (loading) return;
        loading = true;
        const script = document.createElement('script');
        script.type = 'module';
        script.src = SRC;
        document.head.appendChild(script);
    }

    // Trigger 1 — viewport intersection. Works for visible-on-page elements
    // (e.g. ar-experience.html). rootMargin starts the load before the user
    // actually reaches the section. display:none elements (in a closed modal)
    // are intentionally skipped here — they'll be caught by trigger 2.
    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
            (entries, obs) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        load();
                        obs.disconnect();
                        return;
                    }
                }
            },
            { rootMargin: '300px' }
        );
        document.querySelectorAll('model-viewer').forEach((el) => observer.observe(el));
    } else {
        // Old browser fallback — just load it.
        load();
    }

    // Trigger 2 — clicking anything that opens the 3D modal. Use document-
    // delegated capture so the listener works even before the trigger element
    // exists (e.g. inside a cloned skills-carousel item).
    document.addEventListener(
        'click',
        (e) => {
            if (e.target.closest('#viewer-3d-skill')) load();
        },
        true
    );
}

// Lazy-load the mega menu panel. The 127 KB block of polaroid SVGs is desktop-
// only and never visible on initial paint, so we defer it until the browser is
// idle (or the user hovers Services — whichever fires first).
async function initMegaMenuLazy() {
    // Mobile uses the regular dropdown menu, never the mega panel.
    if (window.matchMedia('(max-width: 900px)').matches) return;

    const header = document.querySelector('header');
    const navItem = document.getElementById('servicesNavItem');
    if (!header || !navItem) return;

    let state = 'idle'; // 'idle' | 'loading' | 'loaded'
    let inflight = null;

    async function loadAndInit() {
        if (state === 'loaded') return;
        if (state === 'loading') return inflight;
        state = 'loading';

        inflight = (async () => {
            try {
                const res = await fetch(`components/mega-menu.html?v=${PARTIALS_VERSION}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const html = await res.text();
                // Append at the end of <header> — same DOM position the panel
                // had before extraction (sibling of .header-content).
                header.insertAdjacentHTML('beforeend', html);
                state = 'loaded';
                if (typeof initMegaMenu === 'function') initMegaMenu();
            } catch (err) {
                console.error('Failed to load mega-menu:', err);
                state = 'idle'; // allow retry on next trigger
            }
        })();

        return inflight;
    }

    // Trigger 1 — idle callback (low-priority background fetch).
    if ('requestIdleCallback' in window) {
        requestIdleCallback(() => loadAndInit(), { timeout: 3000 });
    } else {
        setTimeout(loadAndInit, 1500);
    }

    // Trigger 2 — first user interaction with Services nav (covers the case
    // where the user hovers before idle has fired).
    navItem.addEventListener('mouseenter', loadAndInit, { once: true });
    navItem.addEventListener('focusin', loadAndInit, { once: true });
}

// Lazy-load the chat widget — markup fragment + behaviour script. Markup
// goes into a #chat-placeholder div appended to the body if absent. The
// chat-widget.min.js bundle exposes initChatWidget() which wires up events.
async function initChatWidgetLazy() {
    // Create the placeholder if the page didn't bake one in.
    let placeholder = document.getElementById('chat-placeholder');
    if (!placeholder) {
        placeholder = document.createElement('div');
        placeholder.id = 'chat-placeholder';
        document.body.appendChild(placeholder);
    }

    const loaded = await loadComponent('chat-placeholder', `components/chat-widget.html?v=${PARTIALS_VERSION}`);
    if (!loaded) return;

    if (typeof window.initChatWidget === 'function') {
        window.initChatWidget();
        return;
    }

    // Script not yet loaded — pull it in. esbuild emits chat-widget.min.js.
    await new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = `js/chat-widget.min.js?v=${PARTIALS_VERSION}`;
        s.defer = true;
        s.onload = () => {
            if (typeof window.initChatWidget === 'function') window.initChatWidget();
            resolve();
        };
        s.onerror = () => { console.warn('chat-widget.min.js failed to load'); resolve(); };
        document.head.appendChild(s);
    });
}

// Initialize template loading with enhanced error handling
async function initTemplates(currentPage = '') {
    try {
        // Load header and footer components with individual error handling
        // Bust caches on the partials whenever this file changes — the parent
        // page already cache-busts template-loader.js itself.
        const v = PARTIALS_VERSION;
        const [headerLoaded, footerLoaded] = await Promise.all([
            loadComponent('header-placeholder', `components/header.html?v=${v}`),
            loadComponent('footer-placeholder', `components/footer.html?v=${v}`)
        ]);

        // Report loading status
        if (headerLoaded && footerLoaded) {
            console.log('✓ All templates loaded successfully');
        } else {
            console.warn('⚠ Some templates failed to load:', {
                header: headerLoaded ? '✓' : '✗',
                footer: footerLoaded ? '✓' : '✗'
            });
        }

        // Set active navigation state if header loaded successfully
        if (currentPage && headerLoaded) {
            setActiveNavigation(currentPage);
        }

        // Initialize header-dependent functionality after header is loaded
        if (headerLoaded) {
            // Initialize mobile menu (if function exists in main.js)
            // Note: Theme toggle is now in body, so it's initialized during DOMContentLoaded
            if (typeof initMobileMenu === 'function') {
                initMobileMenu();
            }
            // Mega menu is lazy-loaded — see initMegaMenuLazy().
            initMegaMenuLazy();
            // <model-viewer> is also lazy-loaded.
            lazyLoadModelViewer();
            // Initialize Smart scroll header (if function exists in main.js)
            if (typeof initSmartHeader === 'function') {
                initSmartHeader();
            }
        }

        // Inject the natiive Assistant chat widget — production pages only.
        // Opt out by setting `<html data-chat="off">` on the page (none today).
        if (document.documentElement.dataset.chat !== 'off') {
            initChatWidgetLazy();
        }

        return { headerLoaded, footerLoaded };

    } catch (error) {
        console.error('Critical error during template initialization:', error);
        return { headerLoaded: false, footerLoaded: false };
    }
}

// Export for use in pages
window.initTemplates = initTemplates;